window.theme = window.theme || {};

theme.TypeWriterText = (function() {
  class TypeWriterText extends HTMLElement {
    constructor() {
      super();
      this.texts = this.dataset.texts.split(',').map(text => text.trim());
      this.delay = parseFloat(this.dataset.delay) * 1000;
      this.typingSpeed = parseFloat(this.dataset.typingSpeed);  // This is in milliseconds
      this.humanVariance = parseFloat(this.dataset.humanVariance) / 100;
      this.currentIndex = 0;

      Motion.inView(this, this.init.bind(this));
    }

    get cursor() {
      return this._cursor;
    }

    set cursor(el) {
      this._cursor = el;
    }

    async init() {
      this.insertCursor();
      await this.startRotation();
    }

    getRandomDelay() {
      const variance = this.typingSpeed * this.humanVariance;
      return this.typingSpeed + (Math.random() * variance * 2 - variance);
    }

    async typeText(text) {
      let currentText = '';
      for (const char of text) {
        currentText += char;
        this.textContent = currentText;
        // Remove the Motion.animate call since it's forcing a fixed duration
        // Just update the width immediately
        this.style.width = `${this.scrollWidth}px`;
        // Use the configured typing speed for the delay
        await new Promise(resolve => setTimeout(resolve, this.getRandomDelay()));
      }
    }

    async startRotation() {
      while (true) {
        const text = this.texts[this.currentIndex];

        // Clear previous text
        this.textContent = '';
        await Motion.animate(this, { width: 0 }, { duration: 0.1 }).finished;

        // Type out new text
        await this.typeText(text);

        // Show cursor and wait
        this.cursor.classList.add('blink');
        await new Promise(resolve => setTimeout(resolve, this.delay));

        // Hide cursor and prepare for next word
        this.cursor.classList.remove('blink');

        // Move to next text
        this.currentIndex = (this.currentIndex + 1) % this.texts.length;
      }
    }

    insertCursor() {
      if (this.cursor) return;

      this.cursor = document.createElement('span');
      this.cursor.className = 'typed-cursor';
      this.cursor.setAttribute('aria-hidden', true);
      this.cursor.innerHTML = '|';
      this.parentElement.insertBefore(this.cursor, this.nextSibling);
    }
  }

  return TypeWriterText;
})();

// Register custom element
customElements.define('type-writer-text', theme.TypeWriterText);

// Add this after the TypeWriterText component

theme.FreeShippingAnnouncement = (function() {
  class FreeShippingAnnouncement extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.closest('.shopify-section').id.replace('shopify-section-', '');
      this.minimumAmount = parseFloat(this.dataset.minimumAmount);
      this.originalText = this.dataset.text;
      this.isVisible = true;
      this.lastCartTotal = parseFloat(this.dataset.cartTotal) / 100;
      this.bindEvents();
    }

    bindEvents() {
      // Listen for cart updates
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
        theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        (event) => {
          this.onCartUpdate(event);
        }
      );

      // Also listen for the native cart:updated event
      document.addEventListener('cart:updated', (event) => {
        if (event.detail && event.detail.cart) {
          this.onCartUpdate({ cart: event.detail.cart });
        }
      });

      // Listen for slide visibility changes
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          this.isVisible = entry.isIntersecting;
          if (this.isVisible) {
            this.updateShippingMessage(this.lastCartTotal);
          }
        });
      }, { threshold: 0.1 });

      this.observer.observe(this);

      // Listen for section updates
      document.addEventListener('cart:bundled-sections', (event) => {
        event.detail.sections.push(this.sectionId);
      });
    }

    onCartUpdate(event) {
      if (!event.cart) return;
      const cartTotal = event.cart.total_price / 100;
      this.lastCartTotal = cartTotal;
      this.updateShippingMessage(cartTotal);
    }

    updateShippingMessage(cartTotal) {
      const remainingAmount = Math.max(0, this.minimumAmount - cartTotal);
      const shippingMessage = this.querySelector('.shipping-message');

      if (!shippingMessage) return;

      if (remainingAmount <= 0) {
        shippingMessage.innerHTML = this.dataset.congratulationsText;
      } else {
        const formattedAmount = `$${remainingAmount.toFixed(2)}`;
        const messageHTML = this.originalText.replace('[remaining_amount]', `<span class="remaining-amount font-bold">${formattedAmount}</span>`);
        shippingMessage.innerHTML = messageHTML;
      }
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.observer) {
        this.observer.disconnect();
      }
    }
  }

  return FreeShippingAnnouncement;
})();

// Register custom element
customElements.define('free-shipping-announcement', theme.FreeShippingAnnouncement);

theme.AnnouncementCountdownTimer = (function() {
  class AnnouncementCountdownTimer extends HTMLElement {
    constructor() {
      super();
      this.activeMessage = this.getAttribute('data-active-message');
      this.completeMessage = this.getAttribute('data-complete-message');
      this.messageElement = this.parentElement.querySelector('[data-timer-message]');
      this.expirationDate = new Date(this.getAttribute('data-expires')).getTime();
      this.timerInterval = null;

      Motion.inView(this, this.init.bind(this));
    }

    init() {
      this.startCountdown();
    }

    startCountdown() {
      this.updateCountdown();
      this.timerInterval = setInterval(() => {
        this.updateCountdown();
      }, 1000);
    }

    updateCountdown() {
      const now = new Date().getTime();
      const distance = this.expirationDate - now;

      if (distance < 0) {
        this.complete();
        return;
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      this.updateDisplay(days, hours, minutes, seconds);
    }

    updateDisplay(days, hours, minutes, seconds) {
      const items = this.querySelectorAll('.countdown__item p');
      if (items.length === 4) {
        items[0].textContent = days;
        items[1].textContent = hours;
        items[2].textContent = minutes;
        items[3].textContent = seconds;
      }
    }

    complete() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      // Show complete message if available
      if (this.completeMessage && this.messageElement) {
        this.messageElement.innerHTML = this.completeMessage;
      }

      // Hide the timer digits
      this.style.display = 'none';
    }

    disconnectedCallback() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }
    }
  }

  return AnnouncementCountdownTimer;
})();

// Register with a unique name
customElements.define('announcement-countdown-timer', theme.AnnouncementCountdownTimer);
